import React, { useState, useEffect } from 'react';
import { Box, Card, CardActionArea, CardContent, Grid, Typography, Chip, CircularProgress } from '@mui/material';
import { adminSections, AdminSectionKey } from '../../constants/adminSections';
import { getFullEventEditions } from '../../services/eventEditionService';
import { getRegistrationsByEdition } from '../../services/registrationService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';

interface AdminStats {
  registrations: { eventShortName: string; confirmed: number; pending: number }[];
  eventEditions: string;
  actionRequests: number;
  adminTasks: number;
}

interface Props {
  onSelect: (key: AdminSectionKey) => void;
  logCounts?: {
    unresolved: number;
    needsAttention: number;
  };
}

const AdminLandingPanel: React.FC<Props> = ({ onSelect, logCounts }) => {
  const [stats, setStats] = useState<AdminStats>({
    registrations: [],
    eventEditions: '0/0',
    actionRequests: 0,
    adminTasks: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Get future event editions
        const allEditions = await getFullEventEditions();
        const now = new Date();
        const futureEditions = allEditions.filter(edition => 
          edition.endTime && edition.endTime.toDate() > now
        );

        // Calculate registrations stats for future events
        const registrationStats: { eventShortName: string; confirmed: number; pending: number }[] = [];
        
        for (const edition of futureEditions) {
          const registrations = await getRegistrationsByEdition(edition.id);
          const confirmedCount = registrations.filter(reg => reg.status === 'confirmed').length;
          const pendingCount = registrations.filter(reg => reg.status === 'pending').length;
          
          if (confirmedCount > 0 || pendingCount > 0) {
            registrationStats.push({
              eventShortName: edition.eventShortName || edition.eventId,
              confirmed: confirmedCount,
              pending: pendingCount
            });
          }
        }

        // Calculate event editions stats
        const openCount = allEditions.filter(edition => edition.status === 'open').length;
        const futureCount = futureEditions.length;

        // Get action requests count
        const actionRequestsSnapshot = await getDocs(
          query(collection(db, 'actionRequests'), where('status', '==', 'pending'))
        );

        // Get admin tasks count
        const adminTasksSnapshot = await getDocs(
          query(collection(db, 'adminTasks'), where('status', '==', 'open'))
        );

        setStats({
          registrations: registrationStats,
          eventEditions: `${openCount}/${futureCount}`,
          actionRequests: actionRequestsSnapshot.size,
          adminTasks: adminTasksSnapshot.size
        });
      } catch (error) {
        console.error('Error fetching admin stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const getStatForSection = (key: AdminSectionKey): string | number | { eventShortName: string; confirmed: number; pending: number }[] | null => {
    switch (key) {
      case 'registrations':
        return stats.registrations;
      case 'editions':
        return stats.eventEditions;
      case 'actions':
        return stats.actionRequests;
      case 'tasks':
        return stats.adminTasks;
      case 'templates':
      case 'bulkemail':
      case 'codelists':
      case 'logs':
        return null;
      default:
        return '';
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>Admin</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose an admin feature below. You can switch sections anytime using the "Back to Menu" button or the hamburger menu (☰) in the top right.
      </Typography>
      <Grid container spacing={2}>
        {adminSections.map((s) => {
          const stat = getStatForSection(s.key);
          return (
            <Grid item key={s.key} xs={12} sm={6} md={4} lg={3}>
              <Card variant="outlined">
                <CardActionArea onClick={() => onSelect(s.key)}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="h6">{s.label}</Typography>
                      {loading ? (
                        <CircularProgress size={20} />
                      ) : s.key === 'registrations' && Array.isArray(stat) && stat.length > 0 ? (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {stat.map((reg, index) => (
                            <Chip 
                              key={index}
                              label={
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <span>{reg.eventShortName}: {reg.confirmed}/</span>
                                  <span style={{ 
                                    color: reg.pending > 0 ? '#f44336' : 'inherit',
                                    fontWeight: reg.pending > 0 ? 'bold' : 'normal'
                                  }}>
                                    {reg.pending}
                                  </span>
                                </Box>
                              }
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (s.key === 'actions' || s.key === 'tasks') && typeof stat === 'number' && stat > 0 ? (
                        <Chip 
                          label={stat} 
                          size="small" 
                          sx={{
                            backgroundColor: '#f44336',
                            color: '#ffffff',
                            borderColor: '#f44336',
                            fontWeight: 'bold',
                            '&:hover': {
                              backgroundColor: '#d32f2f',
                              borderColor: '#d32f2f'
                            }
                          }}
                        />
                      ) : s.key === 'logs' && logCounts ? (
                        <Box display="flex" gap={0.5}>
                          {logCounts.unresolved > 0 && (
                            <Chip 
                              label={logCounts.unresolved} 
                              size="small" 
                              color="primary"
                              sx={{ fontWeight: 'bold' }}
                            />
                          )}
                          {logCounts.needsAttention > 0 && (
                            <Chip 
                              label={logCounts.needsAttention} 
                              size="small" 
                              color="warning"
                              sx={{ fontWeight: 'bold' }}
                            />
                          )}
                        </Box>
                      ) : typeof stat === 'string' || typeof stat === 'number' ? (
                        <Chip 
                          label={stat} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      ) : null}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {/** Optional short descriptions per section could be added later */}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default AdminLandingPanel;
